import { Layer } from "@common/constants";
import { TintedParticles } from "@common/definitions/obstacles";
import { Numeric } from "@common/utils/math";
import { random, randomRotation } from "@common/utils/random";
import { Vec, type Vector } from "@common/utils/vector";
import { PIXI_SCALE } from "../utils/constants";
import { TrenchioSprite } from "../utils/pixi";
import { ATTIC_DARK_ZINDEX, AtticDark } from "./atticDark";
import { Lighting, multiplyTint } from "./lighting";
import { emissiveFrames } from "./atticDarkLogic";
import { CameraManager } from "./cameraManager";

/** an unchanged value's ease (one function, not one made a particle a frame: 0.98.8) */
const linear = (t: number): number => t;

class ParticleManagerClass {
    readonly particles = new Set<Particle>();
    readonly emitters = new Set<ParticleEmitter>();

    update(delta: number): void {
        for (const particle of this.particles) {
            particle.update(delta);

            if (particle.dead) {
                this.particles.delete(particle);
                particle.image.destroy();
                particle.options.onDeath?.(particle);
            }
        }

        for (const emitter of this.emitters) {
            if (emitter.dead) {
                this.emitters.delete(emitter);
                continue;
            }

            if (emitter.active && emitter.lastSpawn + emitter.delay < Date.now()) {
                this.spawnParticle(emitter.spawnOptions());
                emitter.lastSpawn = Date.now();
            }
        }
    }

    spawnParticle(options: ParticleOptions): Particle {
        const particle = new Particle(options);
        this.particles.add(particle);
        CameraManager.getContainer(options.layer ?? Layer.Ground).addChild(particle.image);
        return particle;
    }

    spawnParticles(count: number, options: () => ParticleOptions): void {
        for (let i = 0; i < count; i++) this.spawnParticle(options());
    }

    addEmitter(options: EmitterOptions): ParticleEmitter {
        const emitter = new ParticleEmitter(options);
        this.emitters.add(emitter);
        return emitter;
    }

    reset(): void {
        // (their sprites too: the layers they're drawn in can outlive a reset, a switch of the view being watched)
        for (const particle of this.particles) particle.image.destroy();
        this.particles.clear();
        for (const emitter of this.emitters) emitter.destroy();
        this.emitters.clear();
    }
}

export const ParticleManager = new ParticleManagerClass();

export type ParticleProperty = {
    readonly start: number
    readonly end: number
    readonly ease?: (x: number) => number
} | number;

export interface ParticleOptions {
    readonly frames: string | readonly string[]
    readonly position: Vector
    readonly speed: Vector
    readonly lifetime: number
    readonly zIndex: number
    readonly layer?: Layer
    readonly scale?: ParticleProperty
    readonly alpha?: ParticleProperty
    readonly rotation?: ParticleProperty
    readonly tint?: number
    /** a light (a flame, a spark, a flash): never shaded by the museum attic's dark (its frames tell, if left out) */
    readonly emissive?: boolean
    /** lit by the light where it is (0.98.7, the smoke: the sun's colour, a fire's warmth, the night's dark); smoke by default */
    readonly lit?: boolean
    readonly onDeath?: (particle: Particle) => void
}

export class Particle {
    position: Vector;
    layer: Layer;
    readonly image: TrenchioSprite;

    private readonly _spawnTime = Date.now();
    get spawnTime(): number { return this._spawnTime; }

    private readonly _deathTime = Date.now();
    get deathTime(): number { return this._deathTime; }

    private _dead = false;
    get dead(): boolean { return this._dead; }

    readonly options: ParticleOptions;

    scale: number;
    alpha: number;
    rotation: number;
    private readonly _baseTint: number;
    private readonly _lit: boolean;

    constructor(options: ParticleOptions) {
        this._deathTime = this._spawnTime + options.lifetime;
        // (0.98.8: its own copy, moved in place each frame; the spawner's position object was shared until the first move)
        this.position = Vec.clone(options.position);
        this.layer = options.layer ?? Layer.Ground;
        const frames = options.frames;
        const frame = typeof frames === "string" ? frames : frames[random(0, frames.length - 1)];
        const tintedParticle = TintedParticles[frame];
        this.image = new TrenchioSprite(tintedParticle?.base ?? frame);
        const tint = options.tint ?? tintedParticle?.tint ?? 0xffffff;
        // (up in the museum's attic, over its dark: as dark as the dark is where it is, unless it's a light. 1.04.2's
        // audit: the bits of a broken crate, a bullet's chips, dust, showed at full brightness in a black room)
        this.image.tint = options.zIndex > ATTIC_DARK_ZINDEX && !(options.emissive ?? emissiveFrames(frames))
            ? AtticDark.shade(tint, options.position, this.layer)
            : tint;
        this._baseTint = this.image.tint;
        this._lit = options.lit ?? frame === "smoke_particle";

        this.scale = typeof options.scale === "number" ? options.scale : options.scale?.start ?? 1;
        this.alpha = typeof options.alpha === "number" ? options.alpha : options.alpha?.start ?? 1;
        this.rotation = typeof options.rotation === "number" ? options.rotation : options.rotation?.start ?? randomRotation();

        this.options = options;

        this._updateImage();
    }

    update(delta: number): void {
        // (0.98.8: moved in place, the same sums: two vectors a particle a frame no more)
        const speed = this.options.speed, step = delta / 1e3;
        this.position.x = this.position.x + speed.x * step;
        this.position.y = this.position.y + speed.y * step;
        const options = this.options;

        const now = Date.now();
        let interpFactor: number;
        if (now >= this._deathTime) {
            this._dead = true;
            interpFactor = 1;
        } else {
            interpFactor = (now - this._spawnTime) / options.lifetime;
        }

        if (typeof options.scale === "object") {
            this.scale = Numeric.lerp(options.scale.start, options.scale.end, (options.scale.ease ?? linear)(interpFactor));
        }

        if (typeof options.alpha === "object") {
            this.alpha = Numeric.lerp(options.alpha.start, options.alpha.end, (options.alpha.ease ?? linear)(interpFactor));
        }

        if (typeof options.rotation === "object") {
            this.rotation = Numeric.lerp(options.rotation.start, options.rotation.end, (options.rotation.ease ?? linear)(interpFactor));
        }

        this._updateImage();
    }

    protected _updateImage(): void {
        // (0.98.7: smoke lit by the light where it drifts)
        if (this._lit) this.image.tint = multiplyTint(this._baseTint, Lighting.litTint(this.position.x, this.position.y, this.layer));
        this.image
            .setZIndex(this.options.zIndex)
            .setPos(this.position.x * PIXI_SCALE, this.position.y * PIXI_SCALE)
            .setScale(this.scale)
            .setRotation(this.rotation)
            .setAlpha(this.alpha);
    }

    kill(): void {
        this._dead = true;
        ParticleManager.particles.delete(this);
        this.image.destroy();
    }
}

export interface EmitterOptions {
    readonly delay: number
    readonly active: boolean
    readonly spawnOptions: () => ParticleOptions
}

export class ParticleEmitter {
    private _dead = false;
    get dead(): boolean { return this._dead; }

    lastSpawn = 0;
    delay: number;
    active: boolean;
    readonly spawnOptions: () => ParticleOptions;

    constructor(options: EmitterOptions) {
        this.delay = options.delay;
        this.active = options.active;
        this.spawnOptions = options.spawnOptions;
    }

    destroy(): void {
        this._dead = true;
    }
}
